import { hasBannedCharacters } from '../../lib/validation/text_validation';
import { isValidBaseReferenceArray } from './base.validator';

/**
 * Validates a company ID string by ensuring it is non-empty and does not contain banned characters.
 * @param {string} company_id The company ID string to validate.
 * @returns {boolean} `true` if the company ID is valid, `false` otherwise.
 */
export const isValidCompanyId = (company_id: unknown): boolean =>
  typeof company_id === 'string' &&
  !!(
    company_id &&
    !hasBannedCharacters(company_id, {
      banned_characters: '0123456789 .()&$+%@'
    })
  );

/**
 * Validates a company name string by ensuring it is non-empty and does not contain banned characters.
 * @param {string} company_name The company name string to validate.
 * @returns {boolean} `true` if the company name is valid, `false` otherwise.
 */
export const isValidCompanyName = (company_name: unknown): boolean =>
  !!(
    company_name &&
    typeof company_name === 'string' &&
    !hasBannedCharacters(company_name, {
      banned_characters: '0123456789,.()&$+%@'
    })
  );

/**
 * Validates an array of company standards, allows for none.
 * @param {Array<unknown>} sectors The company standards array to validate.
 * @returns {boolean} `true` if the company standards are valid, `false` otherwise.
 */
export const isValidCompanyStandards = (standards: unknown): boolean =>
  Array.isArray(standards) && isValidBaseReferenceArray(standards, true);

/**
 * Validates a logo path string by ensuring it is either an empty string or a valid URL.
 * @param {unknown} logo_path The logo path string to validate.
 * @returns {boolean} `true` if the logo path is valid, `false` otherwise.
 */
export const isValidCompanyLogoPath = (logo_path: unknown): boolean => {
  if (typeof logo_path !== 'string') return false;
  if (logo_path === '') return true;

  try {
    const url: URL = new URL(logo_path);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Validates a company client bigquery dataset ID string by ensuring it is non-empty and does not contain banned characters.
 * @param {string} company_id The company client bigquery dataset ID string to validate.
 * @returns {boolean} `true` if the company client bigquery dataset ID is valid, `false` otherwise.
 */
export const isValidCompanyClientBigqueryDatasetId = (
  client_bigquery_dataset_id: unknown
): boolean =>
  typeof client_bigquery_dataset_id === 'string' &&
  !hasBannedCharacters(client_bigquery_dataset_id, {
    banned_characters: "0123456789- .()'&$+%@"
  });
